import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import './auth.css';
import logo from '../../assets/logo.png';

const ResetPassword = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { resetPassword } = useAuth();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [tokenValid, setTokenValid] = useState(null);

  // Enhanced token verification with early return
  useEffect(() => {
    const checkTokenValidity = async () => {
      if (!token) {
        setTokenValid(false);
        setError('No password reset token provided');
        return;
      }

      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000'}/api/password/verify-reset-token/${token}`
        );
        const data = await response.json();
        
        if (response.ok && data.success) {
          setTokenValid(true);
        } else {
          setTokenValid(false);
          setError(data.message || 'Invalid or expired password reset link');
        }
      } catch (err) {
        setTokenValid(false);
        setError('Failed to verify password reset link. Please try again.');
      }
    };

    checkTokenValidity();
  }, [token]);

  // Combined password validation (shows all requirements but updates in real-time)
  const validatePassword = (value) => {
    const requirements = [
      { test: value.length >= 8, message: 'Must be at least 8 characters' },
      { test: /[A-Z]/.test(value), message: 'Include at least one uppercase letter' },
      { test: /[0-9]/.test(value), message: 'Include at least one number' },
      { test: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(value), message: 'Include at least one special character' }
    ];

    const failedRequirements = requirements.filter(req => !req.test);
    return failedRequirements.map(req => req.message).join('. ') + (failedRequirements.length ? '.' : '');
  };

  // Your improved change handlers
  const handlePasswordChange = (e) => {
    const value = e.target.value;
    setPassword(value);
    setPasswordError(validatePassword(value));
    if (confirmPassword && value !== confirmPassword) {
      setPasswordError('Passwords do not match');
    }
  };

  const handleConfirmPasswordChange = (e) => {
    const value = e.target.value;
    setConfirmPassword(value);
    if (password !== value) {
      setPasswordError('Passwords do not match');
    } else {
      setPasswordError(validatePassword(password));
    }
  };

  // Enhanced submit handler with your state reset improvements
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validationError = validatePassword(password);
    if (validationError) {
      setPasswordError(validationError);
      return;
    }

    if (password !== confirmPassword) {
      setPasswordError('Passwords do not match');
      return;
    }

    setIsLoading(true);
    setError('');
    setMessage('');
    setPasswordError('');

    try {
      const response = await resetPassword(token, password);
      
      if (response?.success) {
        setMessage('Password updated successfully! Redirecting to login...');
        setTimeout(() => navigate('/login', { 
          state: { 
            success: 'Password reset successful. Please login with your new password.',
            from: 'password-reset' 
          },
          replace: true 
        }), 2000);
      } else {
        setError(response?.message || 'Failed to reset password. Please try again.');
      }
    } catch (err) {
      const serverMessage = err.response?.data?.message;
      const errorMessage = serverMessage || err.message || 'An unexpected error occurred. Please try again.';
      
      setError(errorMessage);
      
      if (serverMessage?.toLowerCase().includes('invalid') || 
          serverMessage?.toLowerCase().includes('expired')) {
        setTokenValid(false);
      }
    } finally {
      setIsLoading(false);
    }
  };

  /* ---------- Render Sections (Maintaining Your Original UI) ---------- */
  if (tokenValid === null) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-header">
            <img src={logo} alt="Company Logo" style={{ height: '72px' }} />
            <h2>Verifying Password Reset Link</h2>
            <p>Please wait while we verify your password reset link...</p>
          </div>
          <div className="flex justify-center py-4">
            <div className="spinner"></div>
          </div>
        </div>
      </div>
    );
  }

  if (tokenValid === false) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-header">
            <img src={logo} alt="Company Logo" style={{ height: '72px' }} />
            <h2>Invalid Password Reset Link</h2>
          </div>
          
          {error && <div className="error-message">{error}</div>}
          
          <div className="text-center py-4">
            <Link 
              to="/forgot-password" 
              className="login-button"
              style={{ display: 'inline-block', width: 'auto', padding: '12px 24px' }}
            >
              Request New Reset Link
            </Link>
          </div>
          
          <div className="auth-footer">
            <p>Remember your password? <Link to="/login">Sign in</Link></p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <img src={logo} alt="Company Logo" style={{ height: '72px' }} />
          <h2>Create New Password</h2>
          <p>Your new password must be different from previous passwords</p>
        </div>

        {error && <div className="error-message">{error}</div>}
        {message && <div className="success-message">{message}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="password">New Password</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={handlePasswordChange}
              placeholder="Enter new password"
              required
              minLength="8"
              disabled={isLoading}
              autoFocus // Your UX improvement
              autoComplete="new-password"
            />
            {passwordError && (
              <div className="error-text">
                {passwordError}
                <ul className="password-requirements">
                  <li>At least 8 characters</li>
                  <li>At least one uppercase letter</li>
                  <li>At least one number</li>
                  <li>At least one special character</li>
                </ul>
              </div>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm Password</label>
            <input
              type="password"
              id="confirmPassword"
              value={confirmPassword}
              onChange={handleConfirmPasswordChange}
              placeholder="Confirm new password"
              required
              disabled={isLoading}
              autoComplete="new-password"
            />
          </div>

          <button 
            type="submit" 
            className="login-button"
            disabled={isLoading || passwordError || !password || !confirmPassword}
          >
            {isLoading ? (
              <>
                <span className="spinner"></span> Resetting...
              </>
            ) : 'Reset Password'}
          </button>
        </form>

        <div className="auth-footer">
          <p>Remember your password? <Link to="/login">Sign in</Link></p>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;